import {useEffect, useState} from "react";
import {Button, Input, InputGroup, Loader, Modal, RadioTile, RadioTileGroup, Table, Tabs, toaster} from "rsuite";
import Tab from "rsuite/Tab";
import {AppPlatform} from "../../../api/artifacts";
import {ArtifactsApi} from "../../../services/Artifacts.api";
import NotificationFactory from "../../../shared/Notifications";
import {
  APP_PLATFORM_OPTIONS,
  AppChangelogFormState,
  AppChangelogModalProps,
  FirmwareHistoryItem,
  FirmwareModalTab,
} from "../Firmwares.model";
import {FirmwaresUtils} from "../Firmwares.util";

const {Column, HeaderCell, Cell} = Table;

const AppChangelogModal = ({open, mode, changelog, onClose, onSuccess}: AppChangelogModalProps) => {
  const [form, setForm] = useState<AppChangelogFormState>(() => FirmwaresUtils.getAppChangelogInitialState(changelog));
  const [modalChangelog, setModalChangelog] = useState(changelog);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<FirmwareModalTab>("general");
  const [historyItems, setHistoryItems] = useState<FirmwareHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setModalChangelog(null);
      return;
    }

    setModalChangelog(changelog);
    setForm(FirmwaresUtils.getAppChangelogInitialState(changelog));
    setActiveTab("general");
    setHistoryItems([]);
    setHistoryLoaded(false);
    setDeleteConfirmOpen(false);

    if (mode !== "edit" || !changelog?.id) {
      setDetailsLoading(false);
      return;
    }

    let isActual = true;

    setDetailsLoading(true);

    ArtifactsApi.appChangelogs.getById(changelog.id)
      .then((response) => {
        if (!isActual) return;

        const detailedChangelog = response as AppChangelogModalProps["changelog"];

        setModalChangelog(detailedChangelog);
        setForm(FirmwaresUtils.getAppChangelogInitialState(detailedChangelog));
      })
      .catch((error) => {
        if (!isActual) return;

        console.error("Ошибка получения информации о changelog:", error);
        toaster.push(NotificationFactory.error("Ошибка получения информации об изменении"), {placement: "topEnd"});
      })
      .finally(() => {
        if (isActual) {
          setDetailsLoading(false);
        }
      });

    return () => {
      isActual = false;
    };
  }, [open, changelog, mode]);

  const currentChangelog = modalChangelog || changelog;

  const loadHistory = async () => {
    if (!currentChangelog?.id || historyLoaded || historyLoading) return;

    setHistoryLoading(true);

    try {
      const response = await ArtifactsApi.appChangelogs.getHistory(currentChangelog.id);

      setHistoryItems(FirmwaresUtils.getItems<FirmwareHistoryItem>(response));
      setHistoryLoaded(true);
    } catch (error) {
      console.error("Ошибка получения истории изменений приложения:", error);
      toaster.push(NotificationFactory.error("Ошибка получения истории изменений"), {placement: "topEnd"});
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") {
      loadHistory().then();
    }
  }, [activeTab]);

  const handleChange = <K extends keyof AppChangelogFormState>(field: K, value: AppChangelogFormState[K]) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTabSelect = (eventKey?: string | number) => {
    setActiveTab(eventKey === "history" ? "history" : "general");
  };

  const handleSubmit = async () => {
    const missingFields = FirmwaresUtils.getMissingRequiredFields([
      {label: "Версия", value: form.version},
      {label: "Описание", value: form.description},
      {label: "Платформа", value: form.platform},
    ]);

    if (missingFields.length) {
      toaster.push(
        NotificationFactory.error(FirmwaresUtils.getRequiredFieldsMessage(missingFields)),
        {placement: "topEnd"}
      );
      return;
    }

    setLoading(true);

    try {
      if (mode === "create") {
        await ArtifactsApi.appChangelogs.create({
          version: form.version || null,
          description: form.description || null,
          platform: form.platform || undefined,
        });
      } else if (currentChangelog?.id) {
        await ArtifactsApi.appChangelogs.update(currentChangelog.id, {
          version: form.version || null,
          description: form.description || null,
          platform: form.platform || undefined,
        });
      }

      toaster.push(NotificationFactory.success(mode === "create" ? "Изменение добавлено" : "Изменение обновлено"), {placement: "topEnd"});
      onSuccess();
    } catch (error) {
      console.error("Ошибка сохранения изменения приложения:", error);
      toaster.push(NotificationFactory.error(mode === "create" ? "Ошибка добавления изменения" : "Ошибка обновления изменения"), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentChangelog?.id) return;

    setDeleteLoading(true);

    try {
      await ArtifactsApi.appChangelogs.delete(currentChangelog.id);
      toaster.push(NotificationFactory.success("Изменение удалено"), {placement: "topEnd"});
      setDeleteConfirmOpen(false);
      onSuccess();
    } catch (error) {
      console.error("Ошибка удаления изменения приложения:", error);
      toaster.push(NotificationFactory.error("Ошибка удаления изменения"), {placement: "topEnd"});
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderHistory = () => {
    if (mode === "create") {
      return <div className="firmware-modal__history-empty">История появится после создания изменения</div>;
    }

    if (historyLoading) {
      return (
        <div className="firmware-modal__history-loader">
          <Loader content="Загрузка истории..." />
        </div>
      );
    }

    if (!historyItems.length) {
      return <div className="firmware-modal__history-empty">История изменений пуста</div>;
    }

    return (
      <Table autoHeight data={historyItems} rowHeight={60} className="firmware-modal__history-table" wordWrap>
        <Column width={120} verticalAlign="middle" fullText>
          <HeaderCell>Действие</HeaderCell>
          <Cell dataKey="action">
            {(rowData: FirmwareHistoryItem) => rowData.action || "-"}
          </Cell>
        </Column>

        <Column flexGrow={1} minWidth={220} verticalAlign="middle" fullText>
          <HeaderCell>Комментарий</HeaderCell>
          <Cell dataKey="comment">
            {(rowData: FirmwareHistoryItem) => rowData.comment || "-"}
          </Cell>
        </Column>

        <Column width={240} verticalAlign="middle" fullText>
          <HeaderCell>Автор</HeaderCell>
          <Cell dataKey="performedBy">
            {(rowData: FirmwareHistoryItem) => rowData.performedBy || "-"}
          </Cell>
        </Column>

        <Column width={150} verticalAlign="middle" fullText>
          <HeaderCell>Дата</HeaderCell>
          <Cell dataKey="createdAt">
            {(rowData: FirmwareHistoryItem) => FirmwaresUtils.formatDate(rowData.createdAt)}
          </Cell>
        </Column>
      </Table>
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose} size="md" backdrop="static">
        <Modal.Header>
          <Modal.Title>{mode === "create" ? "Добавить изменение" : "Редактировать изменение"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={handleTabSelect}>
            <Tab eventKey="general" title="Общее">
              <InputGroup className="mb-3">
                <InputGroup.Addon>Версия</InputGroup.Addon>
                <Input value={form.version} onChange={(value) => handleChange("version", value)} />
              </InputGroup>

              <InputGroup className="mb-3">
                <Input
                  as="textarea"
                  rows={4}
                  value={form.description}
                  placeholder="Описание"
                  onChange={(value) => handleChange("description", value)}
                />
              </InputGroup>

              <div className="mb-3">
                <RadioTileGroup
                  as="div"
                  aria-label="App platform"
                  className="w-100 firmware-modal__app-platform-group"
                  value={form.platform || undefined}
                  onChange={(value) => handleChange("platform", value as AppPlatform)}
                >
                  {APP_PLATFORM_OPTIONS.map((option) => (
                    <RadioTile
                      key={option.value}
                      className="mb-0"
                      value={option.value}
                      label={option.label}
                    >
                      <span className="radio-tile-text">{option.label}</span>
                    </RadioTile>
                  ))}
                </RadioTileGroup>
              </div>

              {mode === "edit" && (
                <Button
                  appearance="primary"
                  color="red"
                  className="firmware-modal__action-button"
                  disabled={loading || detailsLoading || deleteLoading || !currentChangelog?.id}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Удалить
                </Button>
              )}
            </Tab>

            <Tab eventKey="history" title="История изменений">
              {renderHistory()}
            </Tab>
          </Tabs>
        </Modal.Body>
        {activeTab === "general" && (
          <Modal.Footer>
            <Button appearance="primary" onClick={handleSubmit} loading={loading} disabled={loading || detailsLoading || deleteLoading}>
              Сохранить
            </Button>
            <Button appearance="subtle" onClick={onClose} disabled={loading || deleteLoading}>
              Отмена
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} backdrop="static" size="xs">
        <Modal.Header>
          <Modal.Title>Подтверждение удаления</Modal.Title>
        </Modal.Header>
        <Modal.Body>Удалить изменение? Это действие нельзя отменить.</Modal.Body>
        <Modal.Footer>
          <Button appearance="subtle" onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button appearance="primary" color="red" onClick={handleDelete} loading={deleteLoading} disabled={deleteLoading}>
            Удалить
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AppChangelogModal;
